import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { Modal } from "../../shared/Modal";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { Markdown } from "../../wiki/Markdown";
import { SearchSelect } from "../../waba/MetricsTab";
import {
  FILE_ACCEPT, Pill, SourcePill, errMsg, fileProblem, fmtDateTime, fmtDue, fmtSize, ghostBtn, inputStyle, labelStyle,
  overdueLabel, primaryBtn, todaySp,
} from "../common";
import { UserPicker } from "./KanbanCreateModal";
import {
  PIPE_LABEL, eventText, fmtDuration, kanbanAttachmentUrl, uploadKanbanAttachment, useKanbanOptions,
  type KAttachment, type KDetail, type Ref,
} from "./common";

export function KanbanCardModal({ cardId, onClose }: { cardId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: c, isLoading, error } = useQuery({ queryKey: ["kanban", "detail", cardId], queryFn: () => api.get<KDetail>(`/kanban/${cardId}`) });
  const [tab, setTab] = useState<"detalhes" | "historico">("detalhes");
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["kanban"] });
  const del = useMutation({ mutationFn: () => api.delete(`/kanban/${cardId}`), onSuccess: () => { refresh(); onClose(); } });

  return (
    <Modal onClose={onClose} zIndex={1000}>
      <div style={{ width: 800, maxWidth: "100%", maxHeight: "calc(100vh - 48px)", overflowY: "auto", background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 12, boxShadow: "var(--shadow-modal)", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        {isLoading || !c ? (
          <div style={{ fontSize: 13, color: error ? "var(--danger)" : "var(--ink-muted)" }}>{error ? errMsg(error, "Não foi possível abrir o card.") : "Carregando…"}</div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  <Pill>{PIPE_LABEL[c.pipe]} · {c.stage}</Pill>
                  {c.tipo_label && <Pill color="var(--violet)" bg="var(--violet-soft)" line="var(--violet-soft)">{c.tipo_label}</Pill>}
                  {c.subject && <Pill color="var(--info)" bg="var(--info-soft)" line="var(--info-line)">{c.subject.name}</Pill>}
                  {c.overdue && c.due_date && <Pill color="var(--danger)" bg="var(--danger-soft)" line="var(--danger-line)">{overdueLabel(c.due_date, c.due_time)}</Pill>}
                  <SourcePill t={c} />
                </div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--ink)", lineHeight: 1.35, wordBreak: "break-word" }}>{c.title}</h2>
              </div>
              <button onClick={onClose} aria-label="Fechar" style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 22, lineHeight: 1, cursor: "pointer" }}>×</button>
            </div>

            <div style={{ display: "flex", borderBottom: "1px solid var(--line)" }}>
              {([["detalhes", "Detalhes"], ["historico", `Histórico (${c.events.length})`]] as const).map(([id, label]) => (
                <button key={id} onClick={() => setTab(id)} style={{ padding: "8px 14px", fontSize: 13, background: "none", border: "none", borderBottom: `2px solid ${tab === id ? "var(--brand)" : "transparent"}`, color: tab === id ? "var(--ink)" : "var(--ink-muted)", cursor: "pointer", fontWeight: tab === id ? 600 : 400 }}>{label}</button>
              ))}
            </div>

            {tab === "historico" ? <History c={c} /> : editing ? (
              <EditForm c={c} onDone={() => { setEditing(false); refresh(); }} onCancel={() => setEditing(false)} />
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12, padding: 14, background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10 }}>
                  <Field label="Dono" value={c.owner?.name ?? "—"} />
                  {c.agent && <Field label="Agente" value={c.agent.name} />}
                  <Field label="Prazo" value={c.due_date ? fmtDue(c.due_date, c.due_time) : "Sem prazo"} color={c.overdue ? "var(--danger)" : undefined} />
                  <Field label="Criado por" value={`${c.created_by?.name ?? (c.source_system ? `API - ${c.source_system}` : "API")} · ${fmtDateTime(c.created_at)}`} />
                  <Field label="Nesta etapa desde" value={fmtDateTime(c.stage_changed_at)} />
                  <Field label="Idade" value={fmtDuration(c.age_seconds)} />
                </div>
                <Participants c={c} onChanged={refresh} />
                {c.description && (
                  <section><span style={labelStyle}>Descrição</span><Markdown source={c.description} /></section>
                )}
                <Attachments c={c} onChanged={refresh} />
                {(c.can_edit || c.can_delete) && (
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center", borderTop: "1px solid var(--line)", paddingTop: 16 }}>
                    {c.can_delete && <button onClick={() => setConfirmDelete(true)} style={{ ...ghostBtn, color: "var(--danger)", marginRight: "auto" }}>Excluir</button>}
                    {c.can_edit && <StageSelect c={c} onChanged={refresh} />}
                    {c.can_edit && <button onClick={() => setEditing(true)} style={primaryBtn}>Editar</button>}
                  </div>
                )}
                {del.isError && <span style={{ fontSize: 12, color: "var(--danger)" }}>{errMsg(del.error)}</span>}
              </>
            )}
          </>
        )}
      </div>
      {confirmDelete && (
        <ConfirmDialog title="Excluir card?" message="O card, o histórico e todos os anexos são apagados. Não dá para desfazer." confirmLabel="Excluir" danger onConfirm={() => { setConfirmDelete(false); del.mutate(); }} onCancel={() => setConfirmDelete(false)} />
      )}
    </Modal>
  );
}

function Field({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10.5, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: color ?? "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={value}>{value}</div>
    </div>
  );
}

function StageSelect({ c, onChanged }: { c: KDetail; onChanged: () => void }) {
  const { data: opts } = useKanbanOptions();
  const mut = useMutation({ mutationFn: (stage: string) => api.post(`/kanban/${c.id}/stage`, { stage }), onSuccess: onChanged });
  return (
    <select value={c.stage} onChange={e => mut.mutate(e.target.value)} disabled={mut.isPending} aria-label="Etapa" style={{ ...inputStyle, width: "auto" }}>
      {(opts?.pipes[c.pipe] ?? [c.stage]).map(s => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

function Participants({ c, onChanged }: { c: KDetail; onChanged: () => void }) {
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function add(list: Ref[]) {
    const u = list[list.length - 1];
    if (!u) return;
    try { await api.post(`/kanban/${c.id}/participants`, { user_id: u.id }); setAdding(false); onChanged(); } catch (e) { setErr(errMsg(e)); }
  }
  async function remove(u: Ref) {
    try { await api.delete(`/kanban/${c.id}/participants/${u.id}`); onChanged(); } catch (e) { setErr(errMsg(e)); }
  }
  return (
    <section>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <span style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Participantes ({c.participants.length})</span>
        {c.can_edit && !adding && <button onClick={() => setAdding(true)} style={{ ...ghostBtn, padding: "5px 12px", fontSize: 12 }}>+ Adicionar</button>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {c.participants.map(p => (
          <span key={p.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, padding: "3px 10px", borderRadius: 99, color: "var(--ink)", background: "var(--surface-hover)", border: "1px solid var(--line)" }}>
            {p.name}
            {c.can_edit && <button onClick={() => remove(p)} aria-label={`Remover ${p.name}`} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", padding: 0, fontSize: 13, lineHeight: 1 }}>×</button>}
          </span>
        ))}
        {!c.participants.length && <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>Só o dono.</span>}
      </div>
      {adding && <div style={{ marginTop: 8, maxWidth: 360 }}><UserPicker value={[]} onChange={add} exclude={[c.owner?.id ?? "", ...c.participants.map(p => p.id)]} /></div>}
      {err && <div style={{ fontSize: 12, color: "var(--danger)", marginTop: 6 }}>{err}</div>}
    </section>
  );
}

function EditForm({ c, onDone, onCancel }: { c: KDetail; onDone: () => void; onCancel: () => void }) {
  const { data: opts } = useKanbanOptions();
  const isTask = c.pipe === "tasks";
  const [title, setTitle] = useState(c.title);
  const [description, setDescription] = useState(c.description);
  const [tipo, setTipo] = useState(c.tipo ?? "");
  const [subjectId, setSubjectId] = useState(c.subject?.id ?? "");
  const [agentId, setAgentId] = useState(c.agent?.id ?? "");
  const [owner, setOwner] = useState<Ref[]>(c.owner ? [c.owner] : []);
  const [day, setDay] = useState(c.due_date ?? "");
  const [time, setTime] = useState(c.due_time ?? "");
  const mut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { title, description, due_date: day || null, due_time: day && time ? time : null };
      if (isTask) Object.assign(body, { tipo, subject_id: subjectId, agent_id: agentId });
      if (c.can_pick_owner && owner[0] && owner[0].id !== c.owner?.id) body.owner_id = owner[0].id;
      return api.patch(`/kanban/${c.id}`, body);
    },
    onSuccess: onDone,
  });
  const subjects = (opts?.subjects ?? []).filter(s => s.tipo === tipo);
  if (c.subject && c.subject.id === subjectId && !subjects.some(s => s.id === subjectId)) subjects.push({ ...c.subject, tipo, tipo_label: "", active: false });
  const ok = !!title.trim() && (!isTask || (!!tipo && !!subjectId && !!agentId));
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {isTask && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <span style={labelStyle}>Tipo</span>
            <select value={tipo} onChange={e => { setTipo(e.target.value); setSubjectId(""); }} style={inputStyle}>
              {(opts?.tipos ?? []).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <span style={labelStyle}>Assunto</span>
            <select value={subjectId} onChange={e => setSubjectId(e.target.value)} style={inputStyle}>
              <option value="">Selecione…</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.name}{s.active ? "" : " (inativo)"}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <span style={labelStyle}>Agente</span>
            <SearchSelect value={agentId} onChange={setAgentId} options={(opts?.agents ?? []).map(a => ({ value: a.id, label: a.name }))} placeholder="Selecione o agente" />
          </div>
        </div>
      )}
      <div>
        <span style={labelStyle}>Título</span>
        <input value={title} maxLength={200} onChange={e => setTitle(e.target.value)} style={inputStyle} />
      </div>
      {c.can_pick_owner && (
        <div>
          <span style={labelStyle}>Dono</span>
          <UserPicker value={owner} onChange={v => setOwner(v.slice(-1))} />
        </div>
      )}
      <div>
        <span style={labelStyle}>Prazo</span>
        <div style={{ display: "flex", gap: 8 }}>
          <input type="date" value={day} min={todaySp()} onChange={e => setDay(e.target.value)} style={{ ...inputStyle, width: 170 }} />
          <input type="time" value={time} onChange={e => setTime(e.target.value)} disabled={!day} style={{ ...inputStyle, width: 120 }} aria-label="Hora (opcional)" />
          {day && <button onClick={() => { setDay(""); setTime(""); }} style={{ ...ghostBtn, padding: "6px 10px" }}>Sem prazo</button>}
        </div>
      </div>
      <div>
        <span style={labelStyle}>Descrição (markdown)</span>
        <textarea rows={6} value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, resize: "vertical" }} />
      </div>
      {mut.isError && <span style={{ fontSize: 12, color: "var(--danger)" }}>{errMsg(mut.error)}</span>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={ghostBtn}>Cancelar</button>
        <button onClick={() => mut.mutate()} disabled={!ok || mut.isPending} style={{ ...primaryBtn, opacity: !ok || mut.isPending ? 0.6 : 1 }}>{mut.isPending ? "Salvando…" : "Salvar"}</button>
      </div>
    </section>
  );
}

function History({ c }: { c: KDetail }) {
  const total = c.time_by_stage.reduce((a, s) => a + s.seconds, 0) || 1;
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <span style={labelStyle}>Tempo em cada etapa · total {fmtDuration(c.age_seconds)}</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {c.time_by_stage.map(s => (
            <div key={s.stage} style={{ display: "grid", gridTemplateColumns: "180px 1fr 80px", gap: 10, alignItems: "center", fontSize: 12.5 }}>
              <span style={{ color: s.stage === c.stage ? "var(--ink)" : "var(--ink-secondary)", fontWeight: s.stage === c.stage ? 600 : 400 }}>{s.stage}</span>
              <div style={{ height: 8, background: "var(--surface-hover)", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ width: `${Math.round((s.seconds / total) * 100)}%`, height: "100%", background: s.stage === c.stage ? "var(--brand)" : "var(--ink-dim)" }} />
              </div>
              <span style={{ color: "var(--ink-secondary)", textAlign: "right" }}>{s.seconds ? fmtDuration(s.seconds) : "—"}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <span style={labelStyle}>Linha do tempo</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[...c.events].reverse().map(e => (
            <div key={e.id} style={{ padding: "8px 12px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12.5, display: "flex", gap: 10 }}>
              <span style={{ color: "var(--ink-muted)", whiteSpace: "nowrap" }}>{fmtDateTime(e.at)}</span>
              <span style={{ color: "var(--ink)", flex: 1 }}>{eventText(e)}</span>
              <span style={{ color: "var(--ink-secondary)", whiteSpace: "nowrap" }}>{e.by}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Attachments({ c, onChanged }: { c: KDetail; onChanged: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  async function send(files: FileList | null) {
    if (!files?.length) return;
    setErr(null);
    for (const f of Array.from(files)) {
      const problem = fileProblem(f);
      if (problem) { setErr(problem); continue; }
      setBusy(`Enviando ${f.name}…`);
      try { await uploadKanbanAttachment(c.id, f); } catch (e) { setErr(errMsg(e, `Falha ao enviar ${f.name}.`)); }
    }
    setBusy(null);
    if (input.current) input.current.value = "";
    onChanged();
  }
  async function remove(a: KAttachment) {
    try { await api.delete(`/kanban/${c.id}/attachments/${a.id}`); onChanged(); } catch (e) { setErr(errMsg(e)); }
  }
  if (!c.attachments.length && !c.can_edit) return null;
  return (
    <section>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <span style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Anexos ({c.attachments.length})</span>
        {c.can_edit && (
          <>
            <input ref={input} type="file" multiple accept={FILE_ACCEPT} hidden onChange={e => send(e.target.files)} />
            <button onClick={() => input.current?.click()} disabled={!!busy} style={{ ...ghostBtn, padding: "5px 12px", fontSize: 12 }}>+ Anexar</button>
          </>
        )}
      </div>
      {busy && <div style={{ fontSize: 12, color: "var(--ink-muted)", marginBottom: 8 }}>{busy}</div>}
      {err && <div style={{ fontSize: 12, color: "var(--danger)", marginBottom: 8 }}>{err}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
        {c.attachments.map(a => {
          const url = kanbanAttachmentUrl(c.id, a.id);
          return (
            <div key={a.id} style={{ border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              {a.content_type.startsWith("image/") ? (
                <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={a.filename} loading="lazy" style={{ width: "100%", height: 140, objectFit: "cover", display: "block", background: "var(--surface-hover)" }} /></a>
              ) : a.content_type.startsWith("video/") ? (
                <video src={url} controls preload="metadata" style={{ width: "100%", height: 140, background: "var(--ink)", display: "block" }} />
              ) : (
                <a href={url} target="_blank" rel="noreferrer" style={{ height: 60, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface-hover)", fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)", textDecoration: "none", textTransform: "uppercase" }}>{a.filename.split(".").pop()} · abrir</a>
              )}
              <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={url} target="_blank" rel="noreferrer" title={a.filename} style={{ fontSize: 12, color: "var(--ink)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none" }}>{a.filename}</a>
                  <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>{fmtSize(a.size)} · {a.uploaded_by?.name ?? "API"}</div>
                </div>
                {c.can_edit && <button onClick={() => remove(a)} aria-label={`Remover ${a.filename}`} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 16 }}>×</button>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
